import {Button, Tag, Toggle} from '@/components/atoms'
import {usePromotion} from '@/context/Promotion/PromotionContext'
import {useSegmentsList} from '@/hooks/useSegmentsList'
import {SegmentType} from '@/types/promotion'
import {excludedSegmentOptions, segmentOptions, SegmentTypes} from '@/utils/constants/promotionConstants'
import {useCallback, useMemo, useState} from 'react'
import SegmentModal from '../SegmentModal'
import SegmentGroup from './SegmentGroup'
import './segmentSection.scss'

const SegmentSection = () => {
  const {
    updatePromotion,
    promotion: {segments},
  } = usePromotion()
  const [segmentModalState, setSegmentModalState] = useState({show: false, type: '', dropdownKey: '', groupIndex: null})
  const [showExcludeDropdown, setExcludeDropdown] = useState(false)
  const [modalSelectedSegments, setModalSelectedSegments] = useState(null)
  // Compute segmentType: prefer segments.type, else infer from selected/excluded segments, else default to ALL_CUSTOMERS.
  const segmentType = useMemo(() => {
    // 1. Backend provided type takes precedence - This is not currently implemented in the backend.
    if (segments?.type) {
      return segments.type
    }

    // 2a. Infer from selected segments display groups (preferred)
    if (Array.isArray(segments?.selectedSegments) && segments.selectedSegments.length > 0) {
      // Attempt to use the first group's selectionType if present – otherwise assume SELECTED_GROUPS as a sensible default
      return segments.selectedSegments[0]?.selectionType || SegmentTypes.SELECTED_GROUPS
    }

    // 2b. Infer from excluded segments if present
    if (
      segments?.excludedSegments &&
      Array.isArray(segments.excludedSegments?.segments) &&
      segments.excludedSegments.segments.length > 0
    ) {
      return segments.excludedSegments?.selectionType || SegmentTypes.SELECTED_SEGMENTS
    }

    // 3. No segment data – treat as ALL_CUSTOMERS
    return SegmentTypes.ALL_CUSTOMERS
  }, [segments])
  const disableSegmentsDropdown = segmentType === SegmentTypes?.ALL_CUSTOMERS

  const {segmentGroupData, segmentsGroups} = useSegmentsList()

  // Use selectedSegments directly from the SegmentConfiguration  // Use selectedSegments directly from the SegmentConfiguration (backend format)
  const selectedSegmentGroups = useMemo(() => {
    return segments?.selectedSegments || []
  }, [segments?.selectedSegments])

  // Build excluded segment related derived data – memoised to avoid creating
  // new references on every render which would otherwise trigger effects in
  // child components.
  const exludedSegments = useMemo(() => {
    // Working array of excluded segment objects for rendering
    const excludedSegmentsArray = segments?.excludedSegments?.segments || []
    const excludedSegmentsView = segments?.excludedSegments?.segmentsView || []

    const exludedSegmentsLocal = excludedSegmentsArray.map(segId => {
      const segView = excludedSegmentsView.find(view => view.segmentId === segId) || {}
      const groupId = segView.segmentGroupId || 'individual-segments'
      // resolve name using segmentsGroups list for display
      const segmentName =
        segView.segmentName || segmentsGroups?.find(seg => seg?.segmentId === segId)?.segmentName || `Segment ${segId}`
      return {
        id: segId,
        name: segmentName,
        sourceGroupId: groupId,
        sourceGroupName: segView.segmentGroupId
          ? segmentsGroups?.find(g => g?.segmentId === segId)?.groupName || ''
          : '',
      }
    })

    return exludedSegmentsLocal
  }, [segments?.excludedSegments, segmentsGroups])

  const handleExcludeToggle = checked => {
    setExcludeDropdown(checked)

    updatePromotion(prev => ({
      ...prev,
      segments: {
        ...prev?.segments,
        excludedSegments: checked
          ? {segments: [], segmentsView: [], selectionType: SegmentTypes.SELECTED_SEGMENTS}
          : {},
      },
    }))
  }

  const handleSegmentSelection = (value, groupIndex = null) => {
    const dropdownKey = groupIndex !== null ? 'selectedSegments' : 'excludedSegments'
    // Determine the selection type based on the chosen option – defaults to undefined if none
    const chosenSelectionType = value?.[0]?.id

    // Persist the chosen selection type to the promotion context right away
    updatePromotion(prev => {
      // Start with a shallow copy to avoid direct state mutation
      const updatedPromotion = {...prev}

      // Ensure the segments object exists
      if (!updatedPromotion.segments) {
        updatedPromotion.segments = {
          selectedSegments: [],
          excludedSegments: {},
        }
      }

      if (groupIndex !== null) {
        // ----- Handling for selected segment groups ----- //
        if (!Array.isArray(updatedPromotion.segments.selectedSegments)) {
          updatedPromotion.segments.selectedSegments = []
        }

        // Guarantee an entry for this groupIndex
        while (updatedPromotion.segments.selectedSegments.length <= groupIndex) {
          updatedPromotion.segments.selectedSegments.push({
            segments: [],
            segmentsView: [],
            displayGroupId: String(updatedPromotion.segments.selectedSegments.length + 1),
            selectionType: chosenSelectionType,
          })
        }

        // Get the current group
        const currentGroup = updatedPromotion.segments.selectedSegments[groupIndex]
        
        // Check if selection type is changing
        const isTypeChanging = currentGroup.selectionType && currentGroup.selectionType !== chosenSelectionType
        
        // If type is changing, clear the segments for this group
        if (isTypeChanging) {
          updatedPromotion.segments.selectedSegments[groupIndex] = {
            segments: [],
            segmentsView: [],
            displayGroupId: currentGroup.displayGroupId,
            selectionType: chosenSelectionType,
          }
        } else {
          // Update only the selectionType for this group (leave other props intact)
          updatedPromotion.segments.selectedSegments[groupIndex] = {
            ...currentGroup,
            selectionType: chosenSelectionType,
          }
        }

        // Keep the global segment type in sync with the selection made for this group.
        updatedPromotion.segments.type = chosenSelectionType
      } else {
        // ----- Handling for excluded segments ----- //
        const currentExcluded = updatedPromotion.segments.excludedSegments || {}
        
        // Check if selection type is changing for excluded segments
        const isTypeChanging = currentExcluded.selectionType && currentExcluded.selectionType !== chosenSelectionType
        
        // If type is changing, clear the excluded segments
        if (isTypeChanging) {
          updatedPromotion.segments.excludedSegments = {
            segments: [],
            segmentsView: [],
            selectionType: chosenSelectionType,
          }
        } else if (currentExcluded.selectionType) {
          // Update selection type while preserving existing data
          updatedPromotion.segments.excludedSegments = {
            ...currentExcluded,
            selectionType: chosenSelectionType,
          }
        }
      }

      return updatedPromotion
    })

    // Open the modal as before
    setSegmentModalState({
      show: true,
      type: chosenSelectionType,
      dropdownKey,
      groupIndex,
    })
  }

  const handleSelect = useCallback(value => {
    // Store the selection data from modal temporarily
    setModalSelectedSegments(value)
  }, [])

  const handleRemove = (id, groupIndex = null) => {
    updatePromotion(prev => {
      const updatedPromotion = {
        ...prev,
        segments: {
          ...prev.segments,
        },
      }

      // If removing from a specific group
      if (groupIndex !== null && prev.segments.selectedSegments) {
        const newSelectedSegments = [...(prev.segments.selectedSegments || [])]

        if (newSelectedSegments[groupIndex]) {
          const currentGroup = newSelectedSegments[groupIndex]

          // Remove the segment from both arrays
          const segmentIndex = currentGroup.segments?.indexOf(id) ?? -1
          if (segmentIndex >= 0) {
            const newSegments = [...(currentGroup.segments || [])]
            newSegments.splice(segmentIndex, 1)

            const newSegmentsView = (currentGroup.segmentsView || []).filter(view => view.segmentId !== id)

            // Update the group
            newSelectedSegments[groupIndex] = {
              ...currentGroup,
              segments: newSegments,
              segmentsView: newSegmentsView,
            }

            // Filter out empty groups but keep at least one
            const nonEmptyGroups = newSelectedSegments.filter((group, idx) => {
              if ((group.segments || []).length > 0) return true
              if (newSelectedSegments.every(g => (g.segments || []).length === 0) && idx === 0) return true
              return false
            })

            updatedPromotion.segments.selectedSegments = nonEmptyGroups.length > 0 ? nonEmptyGroups : []
          }
        }
      } else {
        // Default behavior for excluded segments
        const existingExcluded = prev.segments.excludedSegments || {segments: [], segmentsView: []}
        const idx = existingExcluded.segments?.indexOf(id) ?? -1
        if (idx >= 0) {
          const newSegments = [...existingExcluded.segments]
          newSegments.splice(idx, 1)
          const newSegmentsView = existingExcluded.segmentsView?.filter(view => view.segmentId !== id) || []

          // If no segments left, set excludedSegments to empty object
          if (newSegments.length === 0) {
            updatedPromotion.segments.excludedSegments = {}
          } else {
            updatedPromotion.segments.excludedSegments = {
              ...existingExcluded,
              segments: newSegments,
              segmentsView: newSegmentsView,
            }
          }
        }
      }

      return updatedPromotion
    })
  }

  const handleCloseSegmentModal = () => {
    setSegmentModalState(prev => ({...prev, show: false, groupIndex: null}))
    setModalSelectedSegments(null) // Clear temporary selection
  }

  const handleSubmitSegmentModal = useCallback(() => {
    if (modalSelectedSegments && segmentModalState.dropdownKey) {
      const segments = modalSelectedSegments.segments || []
      const segmentsByGroup = modalSelectedSegments.segmentsByGroup || {}

      // Always update the promotion context – even when selections are empty –
      // so that clearing selections is reflected in the UI.
      updatePromotion(prev => {
        const updatedPromotion = {
          ...prev,
        }

        // Handle selectedSegments for specific groups
        if (segmentModalState.dropdownKey === 'selectedSegments' && segmentModalState.groupIndex !== null) {
          // Initialize selectedSegments if it doesn't exist
          if (!updatedPromotion.segments.selectedSegments) {
            updatedPromotion.segments.selectedSegments = []
          }

          // Ensure we have display groups up to the specified index
          while (updatedPromotion.segments.selectedSegments.length <= segmentModalState.groupIndex) {
            updatedPromotion.segments.selectedSegments.push({
              segments: [],
              segmentsView: [],
              displayGroupId: String(updatedPromotion.segments.selectedSegments.length + 1),
              selectionType: 'SELECTED_GROUPS',
            })
          }

          // Get current group at this index
          const currentGroup = updatedPromotion.segments.selectedSegments[segmentModalState.groupIndex]

          // Build fresh arrays from the modal selection so removals are respected
          const newSegmentIds = []
          const newSegmentsView = []

          const add = (seg, groupId = '') => {
            newSegmentIds.push(seg.id)
            newSegmentsView.push({
              segmentId: seg.id,
              segmentGroupId: groupId,
              segmentName: seg.name,
            })
          }

          if (segmentModalState.type === SegmentTypes.SELECTED_SEGMENTS && segments.length > 0) {
            // Individual segments list
            segments.forEach(seg => add(seg))
          } else if (
            segmentModalState.type === SegmentTypes.SELECTED_GROUPS &&
            Object.keys(segmentsByGroup).length > 0
          ) {
            // Segments selected via folders
            Object.entries(segmentsByGroup).forEach(([groupId, groupData]) => {
              groupData.segments.forEach(seg => add(seg, groupId))
            })
          }

          // Update the group with the brand-new selection arrays
          const updatedSelectedSegmentsArray = [...updatedPromotion.segments.selectedSegments]
          updatedSelectedSegmentsArray[segmentModalState.groupIndex] = {
            ...currentGroup,
            segments: newSegmentIds,
            segmentsView: newSegmentsView,
            selectionType: segmentModalState.type,
          }

          // Assign the new array back to maintain immutability.
          updatedPromotion.segments = {
            ...updatedPromotion.segments,
            selectedSegments: updatedSelectedSegmentsArray,
          }
        } else if (segmentModalState.dropdownKey === 'excludedSegments') {
          const existingExcluded = prev.segments?.excludedSegments || {segments: [], segmentsView: []}
          const existingSegmentIds = new Set(existingExcluded.segments || [])
          const existingSegmentsView = [...(existingExcluded.segmentsView || [])]

          // Build arrays with existing segments plus new selections
          const mergedExcludedIds = [...existingSegmentIds]
          const mergedExcludedView = [...existingSegmentsView]

          const addExcluded = (seg, groupId = '') => {
            // Only add if not already present and seg has required properties
            if (seg?.id && !existingSegmentIds.has(seg.id)) {
              mergedExcludedIds.push(seg.id)
              mergedExcludedView.push({segmentId: seg.id, segmentGroupId: groupId, segmentName: seg.name || ''})
            }
          }

          if (segmentModalState.type === SegmentTypes.SELECTED_SEGMENTS) {
            segments?.forEach(seg => addExcluded(seg))
          } else if (segmentModalState.type === SegmentTypes.SELECTED_GROUPS) {
            Object.entries(segmentsByGroup).forEach(([groupId, groupData]) => {
              groupData?.segments?.forEach(seg => addExcluded(seg, groupId))
            })
          }

          // Only add the structure if there are actually excluded segments
          if (mergedExcludedIds.length > 0) {
            updatedPromotion.segments = {
              ...prev.segments,
              excludedSegments: {
                segments: mergedExcludedIds,
                segmentsView: mergedExcludedView,
                selectionType: segmentModalState.type,
              },
            }
          } else {
            // If no excluded segments, just set an empty object
            updatedPromotion.segments = {
              ...prev.segments,
              excludedSegments: {},
            }
          }
        }

        return updatedPromotion
      })
    }

    handleCloseSegmentModal()
  }, [modalSelectedSegments, segmentModalState, updatePromotion])

  const allIncludedSegmentIds = useMemo(() => {
    const segmentIds = new Set()

    selectedSegmentGroups.forEach(apiGroup => {
      if (apiGroup.segments) {
        apiGroup.segments.forEach(segmentId => {
          segmentIds.add(segmentId)
        })
      }
    })

    return Array.from(segmentIds)
  }, [selectedSegmentGroups])

  // Extract all excluded segment IDs
  const allExcludedSegmentIds = useMemo(() => {
    return segments?.excludedSegments?.segments || []
  }, [segments?.excludedSegments])

  const handleSegmentTypeChange = () => {
    const newType =
      segmentType === SegmentTypes.ALL_CUSTOMERS ? SegmentTypes.SELECTED_GROUPS : SegmentTypes.ALL_CUSTOMERS

    updatePromotion(prev => ({
      ...prev,
      segments: {
        ...prev?.segments,
        type: newType,
        selectedSegments: [
          {
            segments: [],
            segmentsView: [],
            selectionType: newType,
            displayGroupId: '1',
          },
        ],
      },
    }))
  }

  const handleAddGroup = () => {
    updatePromotion(prev => ({
      ...prev,
      segments: {
        ...prev.segments,
        selectedSegments: [
          ...(prev.segments.selectedSegments || []),
          {
            segments: [],
            segmentsView: [],
            displayGroupId: String((prev.segments.selectedSegments || []).length + 1),
            selectionType: 'SELECTED_GROUPS',
          },
        ],
      },
    }))
  }

  const handleDeleteGroup = groupIndex => {
    updatePromotion(prev => {
      const newGroups = [...(prev.segments.selectedSegments || [])]

      // Check if we're trying to delete a valid group
      if (groupIndex >= newGroups.length) {
        return prev
      }

      newGroups.splice(groupIndex, 1)

      // Ensure we always have at least one empty group
      if (newGroups.length === 0) {
        // Don't add an empty group - let the UI handle showing an empty state
      }

      // Update displayGroupId for remaining groups
      newGroups.forEach((group, idx) => {
        group.displayGroupId = String(idx + 1)
      })

      return {
        ...prev,
        segments: {
          ...prev.segments,
          selectedSegments: newGroups,
        },
      }
    })
  }

  const isExcludeEnabled = showExcludeDropdown || Object.keys(segments?.excludedSegments ?? {}).length !== 0

  // Ensure we have at least one group when segments are enabled
  const effectiveSegmentGroups =
    selectedSegmentGroups && selectedSegmentGroups.length > 0
      ? selectedSegmentGroups
      : [{segments: [], segmentsView: [], displayGroupId: '1', selectionType: 'SELECTED_GROUPS'}] // Default to one empty group

  return (
    <div className="products-admin-segment-section-wrapper flex flex-col gap-6 rounded-lg bg-[#1D1D1D] p-6">
      <h3 className="text-lg font-semibold">Segments</h3>
      <Toggle
        isDark
        updateValue={handleSegmentTypeChange}
        value={segmentType === SegmentType?.ALL_CUSTOMERS}
        uiSchema={{size: 'small', label: 'All Customers'}}
      />

      {!disableSegmentsDropdown && (
        <>
          {effectiveSegmentGroups.map((segmentGroup, index) => (
            <div key={`group-${index}`}>
              <SegmentGroup
                groupIndex={index}
                segmentGroup={segmentGroup}
                segmentOptions={segmentOptions}
                segmentsGroups={segmentsGroups}
                onSegmentSelection={items => handleSegmentSelection(items, index)}
                onRemove={handleRemove}
                onDeleteGroup={handleDeleteGroup}
                disabled={disableSegmentsDropdown}
              />
              <div className="my-4 border-b border-gray-600"></div>
            </div>
          ))}

          {/* Display group labels horizontally with OR separators */}
          {(effectiveSegmentGroups.length > 1 || segmentType !== SegmentType?.ALL_CUSTOMERS) && (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              {effectiveSegmentGroups.map((_, index) => (
                <div
                  key={`group-label-${index}`}
                  className="products-admin-segment-section-group-label flex items-center gap-3"
                >
                  <Tag text={`Group ${index + 1}`} color="blue" />
                  {index < effectiveSegmentGroups.length - 1 && (
                    <span className="text-sm font-medium text-white">OR</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <Button
            handleButton={handleAddGroup}
            uiSchema={{variant: 'tertiary', title: 'Add Group', isDark: true, iconLeading: 'add_box', size: 'medium'}}
          />

          {segmentType != SegmentType.ALL_CUSTOMERS && (
            <div className="text-xs text-[#EFEFEF]">
              <p className="mb-2 text-gray-50">Within one group, users must be in all segments to see the promotion.</p>
              <p className="text-gray-50">
                When there are multiple groups, users can be in either group to see the promotion.
              </p>
            </div>
          )}
        </>
      )}

      <SegmentModal
        show={segmentModalState.show}
        type={segmentModalState.type}
        onClose={handleCloseSegmentModal}
        onSubmit={handleSubmitSegmentModal}
        onSelect={handleSelect}
        segmentGroupData={segmentGroupData}
        isExcluded={segmentModalState.dropdownKey === 'excludedSegments'}
        existingSelections={
          segmentModalState.groupIndex !== null
            ? selectedSegmentGroups[segmentModalState.groupIndex] || {segments: [], segmentsView: []}
            : segments?.excludedSegments || []
        }
        excludeSegmentIds={
          segmentModalState.dropdownKey === 'excludedSegments' ? allIncludedSegmentIds : allExcludedSegmentIds
        }
      />
      <div className="h-[1px] bg-[#4E4E4E]" />

      <Toggle
        isDark
        updateValue={handleExcludeToggle}
        value={isExcludeEnabled}
        uiSchema={{size: 'small', label: 'Exclude'}}
      />
      {isExcludeEnabled && (
        <>
          <SegmentGroup
            groupIndex={null}
            segmentGroup={segments?.excludedSegments || {segments: [], segmentsView: []}}
            segmentOptions={excludedSegmentOptions}
            segmentsGroups={segmentsGroups}
            onSegmentSelection={items => handleSegmentSelection(items, null)}
            onRemove={id => handleRemove(id, null)}
            onDeleteGroup={null}
            disabled={false}
          />
        </>
      )}
    </div>
  )
}

export default SegmentSection
